/**
 * Advance COD (BoxyCapital × FFC) — merchant-facing types.
 *
 * Shapes mirror core-service resources verbatim
 * (app/Http/Resources/Api/V1/AdvanceCod/*):
 * - money decimals are STRINGS (house decimal:2 cast), e.g. "3000000.00"
 * - merchant-facing batch identifier = platform_code (BXAB-…); ffc ids never
 *   reach this surface.
 */

/** Full batch lifecycle — 11 backend statuses (AdvanceBatchStatus.php). */
export type AdvanceBatchStatus =
  | 'draft'
  | 'submitting'
  | 'pending'
  | 'queued'
  | 'approved'
  | 'rejected'
  | 'partially_approved'
  | 'disbursed'
  | 'in_progress'
  | 'closed'
  | 'failed';

/** FFC merchant program status (FfcMerchantStatus.php). */
export type FfcMerchantStatus = 'active' | 'suspended' | 'rejected';

/** GET /merchants/advance-cod/summary → object (FinancingSummaryResource). */
export interface AdvanceCodSummary {
  is_active: boolean;
  is_enabled: boolean;
  status: FfcMerchantStatus | null;
  assigned_credit_limit: string | null;
  available_credit_limit: string | null;
  max_single_cashout_amount: string | null;
  activated_at: string | null;
  suspended_at: string | null;

  /*
   * v2 design data contract (W6, DESIGN-v2-inventory.md "Data contract").
   * All optional — the tab renders with tolerant fallbacks (missing → 0 /
   * hidden) so it never crashes while the backend summary v2 lands.
   */
  // flags
  ffc_activated?: boolean;
  remittance_locked?: boolean;
  low_balance_alert?: boolean;
  request_status?: string | null;
  /**
   * Server-derived enrolment status the dashboard uses to pick the right panel
   * (core-service FfcSummaryService::deriveFinancingStatus — "status the
   * dashboard uses to pick the right panel"). It collapses financing row +
   * open-request state into one value so panel selection matches the backend
   * exactly, instead of re-deriving it client-side from is_enabled /
   * request_status / status (which diverges for e.g. an FFC-active row with the
   * Boxy toggle off, or an enrolled-but-not-yet-usable row). `rejected` is a
   * permanent decline with no dedicated panel yet. Optional — the tab keeps the
   * client-side derivation as a fallback for a stale backend that omits it.
   */
  financing_status?: 'not_enrolled' | 'pending_activation' | 'active' | 'suspended' | 'rejected';
  // hero
  remaining_eligible_balance?: string | null;
  total_loan?: string | null;
  max_shipment_payout?: string | null;
  // stats
  in_progress_value?: string | null;
  returned_value?: string | null;
  delivered_toward_threshold?: string | null;
  repayment_threshold?: string | null;
  scheduled_pickup_net_value?: string | null;
  // W8 (R13) — merchant enrolled but has no stored payout channel; disbursement
  // is blocked until one is set. Drives the "add a payout method" prompt.
  payment_method_missing?: boolean;
}

/**
 * Activation request lifecycle (AdvanceCodRequestStatus.php). Merchant POST
 * resolves to `forwarded` (eligible, handed to the care team) or `ineligible`;
 * `overridden` is an Ops action; `requested` is the raw pre-forward state.
 */
export type AdvanceCodRequestStatus = 'requested' | 'forwarded' | 'ineligible' | 'overridden';

/** POST /merchants/advance-cod/request → object (RequestResource). */
export interface AdvanceCodActivationRequest {
  uid: string;
  status: AdvanceCodRequestStatus;
  eligibility_snapshot: Record<string, unknown> | null;
  override_reason: string | null;
  overridden_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface AdvanceCodActivationRequestResponse {
  status: string;
  code: number;
  message: string;
  object: AdvanceCodActivationRequest;
}

/** GET /user → object.advance_cod (null when no financing row). */
export interface UserAdvanceCod {
  is_active: boolean;
  is_enabled: boolean;
  status: FfcMerchantStatus;
  assigned_credit_limit: string;
  available_credit_limit: string;
  max_single_cashout_amount: string;
  activated_at: string | null;
}

/** One batch row (BatchResource) — list + base of detail. */
export interface AdvanceBatch {
  uid: string;
  platform_code: string;
  status: AdvanceBatchStatus;
  order_count: number;
  gross_items_amount: string | null;
  total_delivery_fees: string | null;
  net_batch_amount: string | null;
  approved_amount: string | null;
  disbursed_amount: string | null;
  disbursement_channel: string | null;
  disbursement_reference: string | null;
  disbursed_at: string | null;
  delivered_amount: string | null;
  returned_amount: string | null;
  returns_fee: string | null;
  net_deduction_next_cashout: string | null;
  expected_processing_date: string | null;
  created_at: string;

  /*
   * v2 design contract per-batch fields (W6). Optional — the design table
   * falls back to 0 / em-dash when absent.
   */
  received_amount?: string | null;
  repaid_to_ffc_amount?: string | null;
  outstanding_amount?: string | null;
  loan_fee_amount?: string | null;
  /** Friendly EN text (server-mapped, raw string for unknown reasons). */
  rejection_reason?: string | null;
  /**
   * Stable snake_case reason code the dashboard localizes from
   * (`advance_cod_rejection.*`): insufficient_credit | merchant_suspended |
   * merchant_not_found | other. `other` (or an unknown value) falls back to
   * the raw `rejection_reason` string. Null when the batch has no rejection.
   */
  rejection_reason_code?: string | null;
  /** Raw `CODE: message` failure reason — friendly-mapped via merchantFailureReason. */
  failure_reason?: string | null;
}

/** One financed order line inside a batch detail (BatchOrderResource). */
export interface AdvanceBatchOrder {
  order_platform_code: string | null;
  items_amount: string | null;
  delivery_fee: string | null;
  excluded: boolean;
  exclusion_reason: string | null;
  reported_status: 'delivered' | 'returned' | null;
  reported_amount_collected: string | null;
  reported_at: string | null;
}

/** Settlement repayment applied to a batch (BatchShowResource pivot). */
export interface AdvanceBatchSettlement {
  uid: string;
  settlement_date: string | null;
  status: string;
  repayment_amount: string | null;
  settlement_channel: string | null;
  settlement_reference: string | null;
  recorded_at: string | null;
}

/**
 * GET /merchants/advance-cod/batches/{uid} → object (BatchShowResource).
 * Session-12 contract: orders are NOT embedded (batches can hold 500 of
 * them) — `order_count` / `excluded_orders_count` summarize; the rows live
 * on the paginated GET /batches/{uid}/orders endpoint.
 */
export interface AdvanceBatchDetail extends AdvanceBatch {
  available_credit_after: string | null;
  decision_reason: string | null;
  failure_reason: string | null;
  submitted_at: string | null;
  decided_at: string | null;
  closed_at: string | null;
  excluded_orders_count: number;
  settlements: AdvanceBatchSettlement[];
}

/* ── response envelopes (house shape {status, code, message, object}) ── */

export interface AdvanceCodSummaryResponse {
  status: string;
  code: number;
  message: string;
  object: AdvanceCodSummary;
}

export interface AdvanceBatchListResponse {
  status: string;
  code: number;
  message: string;
  object: {
    items: AdvanceBatch[];
    page: number;
    total: number;
    pages: number;
    perPage: number;
    /** per-status bucket counts for the chips row (never includes draft/submitting) */
    status_counts: Partial<Record<AdvanceBatchStatus, number>>;
  };
}

/** GET /merchants/advance-cod/batches/{uid}/orders → house list envelope. */
export interface AdvanceBatchOrdersResponse {
  status: string;
  code: number;
  message: string;
  object: {
    items: AdvanceBatchOrder[];
    page: number;
    total: number;
    pages: number;
    perPage: number;
  };
}

export interface AdvanceBatchDetailResponse {
  status: string;
  code: number;
  message: string;
  object: AdvanceBatchDetail;
}

/**
 * GET /merchants/advance-cod/transfers → one settlement transfer to FFC
 * (W6, "Transfers to FFC" list). Money fields are house decimal strings.
 */
export interface FfcTransfer {
  uid?: string;
  reference: string | null;
  date: string | null;
  batches_covered_count: number | null;
  amount: string | null;
  credit_restored_amount: string | null;
}

export interface FfcTransfersResponse {
  status: string;
  code: number;
  message: string;
  object: {
    items: FfcTransfer[];
    page: number;
    total: number;
    pages: number;
    perPage: number;
  };
}

/** Batches index filters (BatchFilterRequest). */
export interface AdvanceBatchFilters {
  filter_status?: AdvanceBatchStatus;
  filter_created_at_from?: string;
  filter_created_at_to?: string;
  filter_platform_code?: string;
}

/** FFC mirror-ledger transaction subjects (TransactionSubject::ffcSubjects). */
export const FFC_LEDGER_SUBJECTS = [
  'ffc_advance',
  'ffc_settlement',
  'ffc_returns_fee',
  'ffc_deduction',
] as const;

export type FfcLedgerSubject = (typeof FFC_LEDGER_SUBJECTS)[number];
